import * as Types from '../services/api/api.types';

/**
 * The filter state a category listing is narrowed by, and the faceted counting
 * that drives its sidebar. Kept apart from the page so the counting rules - the
 * fiddly part - can be read and tested on their own.
 */

export interface CategoryFilterState {
    manufacturers: string[];
    /** Spec name -> the values ticked under it. A product matches any of them. */
    attributes: Record<string, string[]>;
    /** Warranty terms, in months. */
    warranties: number[];
    priceMin: number | null;
    priceMax: number | null;
    inStockOnly: boolean;
    promoOnly: boolean;
    /**
     * Keep products that never state a spec, instead of dropping them from a
     * spec filter. The suppliers describe the same property under different
     * names, so a spec is usually carried by only one of them - without this,
     * ticking one silently discards everyone else's products.
     */
    includeUnspecified: boolean;
    /** Free text matched against the product name and model. */
    query: string;
}

export const EMPTY_FILTERS: CategoryFilterState = {
    manufacturers: [],
    attributes: {},
    warranties: [],
    priceMin: null,
    priceMax: null,
    inStockOnly: false,
    promoOnly: false,
    includeUnspecified: false,
    query: '',
};

/** Identifies one filter, for the count that has to ignore it. */
export const MANUFACTURER_DIMENSION = 'manufacturer';
export const PRICE_DIMENSION = 'price';
export const WARRANTY_DIMENSION = 'warranty';
export const STOCK_DIMENSION = 'stock';
export const PROMO_DIMENSION = 'promo';
export const QUERY_DIMENSION = 'query';
export const attributeDimension = (name: string): string => `attr:${name}`;

export function effectivePrice(product: Types.ProductFull): number {
    return product.price_promo ?? product.price_partner;
}

export function isInStock(product: Types.ProductFull): boolean {
    return product.status === 1;
}

export function isOnPromo(product: Types.ProductFull): boolean {
    return product.price_promo != null && product.price_promo < product.price_partner;
}

function bgName(product: Types.ProductFull): string {
    return product.name?.find(n => n.language_code === 'bg')?.text ?? '';
}

function warrantyOf(product: Types.ProductFull): number | null {
    const months = Number(product.warranty);
    return Number.isFinite(months) && months > 0 ? months : null;
}

type Predicate = (product: Types.ProductFull) => boolean;

/**
 * One predicate per active filter, keyed by the dimension it belongs to. A
 * dimension with nothing selected contributes nothing, so an untouched sidebar
 * leaves the listing whole.
 */
function buildPredicates(filters: CategoryFilterState): Map<string, Predicate> {
    const predicates = new Map<string, Predicate>();

    if (filters.manufacturers.length > 0) {
        const wanted = new Set(filters.manufacturers);
        predicates.set(MANUFACTURER_DIMENSION, p => !!p.manufacturer && wanted.has(p.manufacturer));
    }

    if (filters.priceMin !== null || filters.priceMax !== null) {
        predicates.set(PRICE_DIMENSION, p => {
            const price = effectivePrice(p);
            if (filters.priceMin !== null && price < filters.priceMin) return false;
            if (filters.priceMax !== null && price > filters.priceMax) return false;
            return true;
        });
    }

    if (filters.warranties.length > 0) {
        const wanted = new Set(filters.warranties);
        predicates.set(WARRANTY_DIMENSION, p => {
            const months = warrantyOf(p);
            return months !== null && wanted.has(months);
        });
    }

    if (filters.inStockOnly) predicates.set(STOCK_DIMENSION, isInStock);
    if (filters.promoOnly) predicates.set(PROMO_DIMENSION, isOnPromo);

    const query = filters.query.trim().toLowerCase();
    if (query) {
        const terms = query.split(/\s+/);
        predicates.set(QUERY_DIMENSION, p => {
            const haystack = `${bgName(p)} ${p.model ?? ''}`.toLowerCase();
            return terms.every(term => haystack.includes(term));
        });
    }

    for (const [name, values] of Object.entries(filters.attributes)) {
        if (values.length === 0) continue;
        const wanted = new Set(values);
        predicates.set(attributeDimension(name), p => {
            const stated = (p.attributes ?? []).filter(a => a.name === name);
            // Silent about the spec is not the same as failing it.
            if (stated.length === 0) return filters.includeUnspecified;
            return stated.some(a => wanted.has(a.value));
        });
    }

    return predicates;
}

function matches(
    product: Types.ProductFull,
    predicates: Map<string, Predicate>,
    skip?: string,
): boolean {
    for (const [dimension, predicate] of Array.from(predicates.entries())) {
        if (dimension === skip) continue;
        if (!predicate(product)) return false;
    }
    return true;
}

export function applyFilters(
    products: Types.ProductFull[],
    filters: CategoryFilterState,
): Types.ProductFull[] {
    const predicates = buildPredicates(filters);
    if (predicates.size === 0) return products;
    return products.filter(product => matches(product, predicates));
}

export interface FacetOption {
    value: string;
    count: number;
    selected: boolean;
}

export interface Facet {
    name: string;
    options: FacetOption[];
    /** How many of the products below carry this spec at all. */
    withSpec: number;
    /** The listing this facet is counted against: everything the other filters leave. */
    total: number;
}

/**
 * Every option is counted against the listing narrowed by the *other* filters,
 * not by all of them. That is what lets a shopper widen a choice already made:
 * ticking a second manufacturer has to show what it would add, and counting it
 * against itself would always read zero.
 */
function countBy(
    products: Types.ProductFull[],
    predicates: Map<string, Predicate>,
    dimension: string,
    keysOf: (product: Types.ProductFull) => string[],
): Map<string, number> {
    const counts = new Map<string, number>();
    for (const product of products) {
        if (!matches(product, predicates, dimension)) continue;
        // A product counts once per option, even if it lists that option twice.
        const keys = new Set(keysOf(product));
        keys.forEach(key => counts.set(key, (counts.get(key) ?? 0) + 1));
    }
    return counts;
}

/** Options left with nothing under the other filters are dropped, unless ticked. */
function toOptions(
    counts: Map<string, number>,
    selected: string[],
    compare: (a: string, b: string) => number,
): FacetOption[] {
    const chosen = new Set(selected);
    const values = new Set<string>(Array.from(counts.keys()).concat(selected));
    return Array.from(values)
        .filter(value => (counts.get(value) ?? 0) > 0 || chosen.has(value))
        .sort(compare)
        .map(value => ({
            value,
            count: counts.get(value) ?? 0,
            selected: chosen.has(value),
        }));
}

const byLabel = (a: string, b: string) => a.localeCompare(b, 'bg');

/**
 * Spec values are frequently a number with a unit ("8 GB", "128 GB"), which
 * sorts wrongly as text. Leading numbers order numerically; anything else falls
 * back to the Bulgarian collation.
 */
const bySpecValue = (a: string, b: string) => {
    const numberA = parseFloat(a);
    const numberB = parseFloat(b);
    if (Number.isFinite(numberA) && Number.isFinite(numberB) && numberA !== numberB) {
        return numberA - numberB;
    }
    return byLabel(a, b);
};

export interface CategoryFacets {
    manufacturers: FacetOption[];
    warranties: FacetOption[];
    attributes: Facet[];
    inStockCount: number;
    promoCount: number;
    /** The listing's price span under every other filter, for the inputs' hints. */
    priceBounds: { min: number; max: number } | null;
}

export function buildFacets(
    products: Types.ProductFull[],
    filters: CategoryFilterState,
): CategoryFacets {
    const predicates = buildPredicates(filters);

    const manufacturerCounts = countBy(products, predicates, MANUFACTURER_DIMENSION, p =>
        p.manufacturer ? [p.manufacturer] : [],
    );

    const warrantyCounts = countBy(products, predicates, WARRANTY_DIMENSION, p => {
        const months = warrantyOf(p);
        return months !== null ? [String(months)] : [];
    });

    // Spec names arrive already narrowed to this category's facets, so whatever
    // is present is worth offering.
    const attributeNames = new Set<string>();
    products.forEach(p => (p.attributes ?? []).forEach(a => attributeNames.add(a.name)));

    const attributes: Facet[] = Array.from(attributeNames)
        .sort(byLabel)
        .map(name => {
            const dimension = attributeDimension(name);
            const counts = countBy(products, predicates, dimension, p =>
                (p.attributes ?? []).filter(a => a.name === name).map(a => a.value),
            );

            // Counted against the same listing as the options, so the coverage
            // shown always refers to what is actually on screen.
            let withSpec = 0;
            let total = 0;
            for (const product of products) {
                if (!matches(product, predicates, dimension)) continue;
                total++;
                if ((product.attributes ?? []).some(a => a.name === name)) withSpec++;
            }

            return {
                name,
                options: toOptions(counts, filters.attributes[name] ?? [], bySpecValue),
                withSpec,
                total,
            };
        })
        .filter(facet => facet.options.length > 1);

    let inStockCount = 0;
    let promoCount = 0;
    let min = Infinity;
    let max = -Infinity;

    for (const product of products) {
        if (isInStock(product) && matches(product, predicates, STOCK_DIMENSION)) inStockCount++;
        if (isOnPromo(product) && matches(product, predicates, PROMO_DIMENSION)) promoCount++;

        if (matches(product, predicates, PRICE_DIMENSION)) {
            const price = effectivePrice(product);
            if (Number.isFinite(price)) {
                if (price < min) min = price;
                if (price > max) max = price;
            }
        }
    }

    return {
        manufacturers: toOptions(manufacturerCounts, filters.manufacturers, byLabel),
        warranties: toOptions(
            warrantyCounts,
            filters.warranties.map(String),
            (a, b) => Number(a) - Number(b),
        ),
        attributes,
        inStockCount,
        promoCount,
        priceBounds: min <= max ? { min: Math.floor(min), max: Math.ceil(max) } : null,
    };
}

export interface ActiveFilterChip {
    /** Distinguishes chips whose labels could otherwise coincide. */
    key: string;
    label: string;
    /** Clears just this one selection. */
    remove: (filters: CategoryFilterState) => CategoryFilterState;
}

/** The active selections, as removable chips above the listing. */
export function activeFilterChips(filters: CategoryFilterState): ActiveFilterChip[] {
    const chips: ActiveFilterChip[] = [];

    const query = filters.query.trim();
    if (query) {
        chips.push({ key: 'query', label: `„${query}“`, remove: f => ({ ...f, query: '' }) });
    }

    if (filters.inStockOnly) {
        chips.push({ key: 'stock', label: 'Само налични', remove: f => ({ ...f, inStockOnly: false }) });
    }

    if (filters.promoOnly) {
        chips.push({ key: 'promo', label: 'В промоция', remove: f => ({ ...f, promoOnly: false }) });
    }

    filters.manufacturers.forEach(manufacturer => {
        chips.push({
            key: `manufacturer:${manufacturer}`,
            label: manufacturer,
            remove: f => ({ ...f, manufacturers: f.manufacturers.filter(m => m !== manufacturer) }),
        });
    });

    Object.entries(filters.attributes).forEach(([name, values]) => {
        values.forEach(value => {
            chips.push({
                key: `attr:${name}:${value}`,
                label: `${name}: ${value}`,
                remove: f => {
                    const kept = (f.attributes[name] ?? []).filter(v => v !== value);
                    const attributes = { ...f.attributes };
                    if (kept.length > 0) attributes[name] = kept;
                    else delete attributes[name];
                    return { ...f, attributes };
                },
            });
        });
    });

    filters.warranties.forEach(months => {
        chips.push({
            key: `warranty:${months}`,
            label: `Гаранция: ${months} мес.`,
            remove: f => ({ ...f, warranties: f.warranties.filter(w => w !== months) }),
        });
    });

    if (filters.priceMin !== null || filters.priceMax !== null) {
        const from = filters.priceMin !== null ? String(filters.priceMin) : '0';
        const to = filters.priceMax !== null ? String(filters.priceMax) : '∞';
        chips.push({
            key: 'price',
            label: `Цена: ${from} - ${to} €`,
            remove: f => ({ ...f, priceMin: null, priceMax: null }),
        });
    }

    return chips;
}

/**
 * How many more products the listing would show if spec filters kept the ones
 * that never state the spec. Zero unless a spec filter is actually active.
 */
export function countHiddenByMissingSpec(
    products: Types.ProductFull[],
    filters: CategoryFilterState,
): number {
    const specFiltered = Object.values(filters.attributes).some(values => values.length > 0);
    if (!specFiltered) return 0;

    const shown = applyFilters(products, { ...filters, includeUnspecified: false }).length;
    const withUnspecified = applyFilters(products, { ...filters, includeUnspecified: true }).length;
    return withUnspecified - shown;
}

export function countActiveFilters(filters: CategoryFilterState): number {
    return activeFilterChips(filters).length;
}

export function hasActiveFilters(filters: CategoryFilterState): boolean {
    return countActiveFilters(filters) > 0;
}

/**
 * Drops spec selections this category no longer offers. A restored snapshot can
 * name a spec the feed has since stopped carrying, and a filter with no matching
 * checkbox would empty the listing with nothing on screen to explain why.
 */
export function pruneAttributes(
    filters: CategoryFilterState,
    products: Types.ProductFull[],
): CategoryFilterState {
    const available = new Set<string>();
    products.forEach(p => (p.attributes ?? []).forEach(a => available.add(`${a.name} ${a.value}`)));

    const attributes: Record<string, string[]> = {};
    for (const [name, values] of Object.entries(filters.attributes)) {
        const kept = values.filter(value => available.has(`${name} ${value}`));
        if (kept.length > 0) attributes[name] = kept;
    }
    return { ...filters, attributes };
}
